import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { PaginationFooter } from '../shared/PaginationFooter';
import { useDebouncedValue } from '../shared/useUrlPagination';
import { toast } from 'sonner';
import { formatDT, getFileTimestamp } from '../../utils/timezone';
import { Loader2, Plus, UserPlus, ScanLine, RefreshCw, Search, X, Upload, ChevronsUpDown, ChevronDown, Layers } from 'lucide-react';

const PERSON_GROUPS = [
  { type: 'driver', label: 'Drivers' },
  { type: 'staff', label: 'Staff' },
  { type: 'passenger', label: 'Passengers' },
];

const PersonCombobox = ({ persons, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const [type, id] = value.split('::');
    return (persons[type] || []).find((p) => p.id === id)?.full_name || null;
  }, [value, persons]);
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" role="combobox" className="h-7 text-[11px] justify-between max-w-[170px] font-normal" data-testid={testId}>
          <span className="truncate">{selectedLabel || 'Allocate to…'}</span>
          <ChevronsUpDown className="w-3 h-3 ml-1 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0 z-[10000]" align="start">
        <Command>
          <CommandInput placeholder="Search person…" className="h-8 text-xs" data-testid={`${testId}-search`} />
          <CommandList className="max-h-56">
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">No person without a card found.</CommandEmpty>
            {PERSON_GROUPS.map(({ type, label }) => (
              (persons[type] || []).length > 0 && (
                <CommandGroup key={type} heading={label}>
                  {persons[type].map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`${p.full_name} ${p.employee_id || p.passenger_id || ''}`}
                      className="text-xs"
                      onSelect={() => { onChange(`${type}::${p.id}`); setOpen(false); }}
                    >
                      {p.full_name}
                      {(p.employee_id || p.passenger_id) && (
                        <span className="ml-auto text-[10px] text-muted-foreground">{p.employee_id || p.passenger_id}</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const UnmatchedScansModal = ({ open, onOpenChange, api, onChanged, initialCredential = '' }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [persons, setPersons] = useState({ driver: [], staff: [], passenger: [] });
  const [selections, setSelections] = useState({});
  const [labels, setLabels] = useState({});
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectBusy, setSelectBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('skip', (page - 1) * pageSize);
      p.set('limit', pageSize);
      if (debouncedSearch.trim()) p.set('search', debouncedSearch.trim());
      const res = await api.get(`/people/rfid-ibutton-cards/unmatched-scans?${p.toString()}`);
      setRows(res.data || []);
      setTotal(parseInt(res.headers['x-total-count'] || '0', 10));
    } catch {
      toast.error('Failed to load unmatched scans');
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, debouncedSearch]);

  const fetchPersons = useCallback(async () => {
    const [dRes, sRes, pRes] = await Promise.all([
      api.get('/drivers').catch(() => ({ data: [] })),
      api.get('/staff').catch(() => ({ data: [] })),
      api.get('/passengers').catch(() => ({ data: [] })),
    ]);
    const eligible = (list) => (list || []).filter((p) => !p.rfid_ibutton_card_id);
    setPersons({ driver: eligible(dRes.data), staff: eligible(sRes.data), passenger: eligible(pRes.data) });
  }, [api]);

  useEffect(() => {
    if (open) { setSelections({}); setLabels({}); setSelectedKeys([]); setSearch(initialCredential || ''); setPage(1); fetchPersons(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCredential]);
  useEffect(() => { if (open) fetchRows(); }, [open, fetchRows]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const refresh = () => { fetchRows(); fetchPersons(); };
  const pageKeys = rows.map((r) => r.credential_normalized);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.includes(k));
  const toggleAll = (checked) => {
    setSelectedKeys((prev) => checked
      ? [...new Set([...prev, ...pageKeys])]
      : prev.filter((k) => !pageKeys.includes(k)));
  };
  const toggleOne = (key, checked) => {
    setSelectedKeys((prev) => checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key));
  };

  // Server-wide Select All / Deselect All — mirrors the RFID Cards selection bar.
  const selectAllServer = async () => {
    if (selectedKeys.length >= total) { setSelectedKeys([]); return; }
    setSelectBusy(true);
    try {
      const p = new URLSearchParams();
      if (debouncedSearch.trim()) p.set('search', debouncedSearch.trim());
      const res = await api.get(`/people/rfid-ibutton-cards/unmatched-scans?${p.toString()}`);
      setSelectedKeys((res.data || []).map((r) => r.credential_normalized));
    } catch {
      toast.error('Failed to select all');
    } finally {
      setSelectBusy(false);
    }
  };

  const register = async (row, withPerson) => {
    const key = row.credential_normalized;
    let personType = null; let personId = null;
    if (withPerson) {
      const sel = selections[key];
      if (!sel) { toast.error('Select a person first'); return; }
      [personType, personId] = sel.split('::');
    }
    setBusyKey(key);
    try {
      await api.post('/people/rfid-ibutton-cards/register-from-scan', {
        card_value: row.credential,
        label: (labels[key] || '').trim() || null,
        person_type: personType,
        person_id: personId,
      });
      toast.success(withPerson ? 'Card added and allocated' : 'Card added to the RFID / iButton list');
      setSelectedKeys((prev) => prev.filter((k) => k !== key));
      fetchRows();
      if (withPerson) fetchPersons();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to register card');
    } finally {
      setBusyKey(null);
    }
  };

  const fetchAllForKeys = async (keys) => {
    const res = await api.get('/people/rfid-ibutton-cards/unmatched-scans');
    const all = res.data || [];
    return keys ? all.filter((r) => keys.includes(r.credential_normalized)) : all;
  };

  const bulkAdd = async (withAllocations) => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    try {
      const targets = await fetchAllForKeys(selectedKeys);
      const res = await api.post('/people/rfid-ibutton-cards/register-from-scan-bulk', {
        items: targets.map((r) => {
          const key = r.credential_normalized;
          const sel = withAllocations ? selections[key] : null;
          const [personType, personId] = sel ? sel.split('::') : [null, null];
          return {
            card_value: r.credential,
            label: (labels[key] || '').trim() || null,
            person_type: personType,
            person_id: personId,
          };
        }),
      });
      const { created = 0, skipped = 0, allocated = 0, errors = [] } = res.data || {};
      toast.success(`${created} card(s) added${allocated ? `, ${allocated} allocated` : ''}${skipped ? `, ${skipped} skipped` : ''}`);
      if (errors.length > 0) toast.warning(`${errors.length} issue(s) — e.g. ${errors[0]?.error}`);
      setSelectedKeys([]);
      fetchRows();
      if (allocated > 0) fetchPersons();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk add failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const data = await fetchAllForKeys(selectedKeys);
      if (data.length === 0) { toast.error('Nothing to export'); return; }
      // Unified, import-ready format (same headers as the All Cards export). Notes last.
      const headers = ['card_value', 'label', 'status', 'assigned_type', 'assigned_person', 'notes'];
      const lines = data.map((r) => {
        const label = (labels[r.credential_normalized] || '').trim() || r.credential;
        const notes = `Scanned in ${(r.vehicles || []).join(' / ')}; last seen ${formatDT(r.last_seen)}`;
        return [r.credential, label, 'available', '', '', notes].map((v) => `${v ?? ''}`.replace(/,/g, ' ')).join(',');
      });
      const csv = [headers.join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `unmatched-rfid-scans_export_${getFileTimestamp()}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${data.length} credential(s) — fill assigned_type / assigned_person to allocate on import`);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="unmatched-scans-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> Unmatched RFID / iButton scans
          </DialogTitle>
          <DialogDescription>
            Credentials scanned in your vehicles over the last 30 days that are not registered to your account.
            Add them to the card list (with an optional label), allocate to a person, or export for bulk import.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search credential..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-10 pr-9"
              data-testid="unmatched-scans-search"
            />
            {search && (
              <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearch('')} data-testid="unmatched-scans-clear-search">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={selectedKeys.length === 0} data-testid="unmatched-scans-export-button">
            <Upload className="w-4 h-4 mr-2" />
            {selectedKeys.length > 0 ? `Export (${selectedKeys.length})` : 'Export'}
          </Button>
          {selectedKeys.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" disabled={bulkBusy} data-testid="unmatched-scans-bulk-add-button">
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
                  Add selected ({selectedKeys.length})
                  <ChevronDown className="w-3.5 h-3.5 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[10000]">
                <DropdownMenuItem onClick={() => bulkAdd(false)} data-testid="unmatched-scans-bulk-add-only">
                  <Plus className="w-4 h-4 mr-2" /> Add selected (cards only)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => bulkAdd(true)} data-testid="unmatched-scans-bulk-add-allocate">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add &amp; allocate selected
                  <span className="ml-2 text-[10px] text-muted-foreground">
                    ({selectedKeys.filter((k) => selections[k]).length} with person)
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="unmatched-scans-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {/* Selection bar — app-wide standard: count + server-wide Select All + Clear */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="unmatched-scans-selection-bar">
          <span data-testid="unmatched-scans-selection-count">{selectedKeys.length} selected</span>
          {selectedKeys.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={selectBusy} onClick={selectAllServer} data-testid="unmatched-scans-select-all-toggle">
              {selectBusy ? 'Selecting…' : (selectedKeys.length >= total ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedKeys([])} data-testid="unmatched-scans-selection-clear">Clear</Button>
          </>)}
        </div>
        <div className="overflow-x-auto border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-10 px-3">
                  <Checkbox checked={allPageSelected} onCheckedChange={toggleAll} data-testid="unmatched-scans-select-all" />
                </TableHead>
                <TableHead className="px-3">Credential</TableHead>
                <TableHead className="px-3">Vehicle(s)</TableHead>
                <TableHead className="px-3">Last seen</TableHead>
                <TableHead className="px-3 w-16">Scans</TableHead>
                <TableHead className="px-3 w-40">Label</TableHead>
                <TableHead className="px-3 w-[360px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, idx) => {
                const key = row.credential_normalized;
                const busy = busyKey === key;
                const highlighted = !!initialCredential && (
                  (row.credential || '').toLowerCase() === initialCredential.toLowerCase() ||
                  (key || '').toLowerCase() === initialCredential.toLowerCase());
                return (
                  <TableRow key={key} className={`text-xs ${highlighted ? 'bg-amber-100/70 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/40' : ''}`} data-testid={highlighted ? `unmatched-scan-row-highlighted-${idx}` : `unmatched-scan-row-${idx}`}>
                    <TableCell className="px-3">
                      <Checkbox checked={selectedKeys.includes(key)} onCheckedChange={(c) => toggleOne(key, c)} data-testid={`unmatched-scan-select-${idx}`} />
                    </TableCell>
                    <TableCell className="px-3 font-mono break-all" data-testid={`unmatched-scan-credential-${idx}`}>
                      {row.credential}
                      {(row.io_names || []).length > 0 && (
                        <span className="block text-[10px] text-muted-foreground font-sans">{row.io_names.join(', ')}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 break-words">{(row.vehicles || []).join(', ') || '-'}</TableCell>
                    <TableCell className="px-3 whitespace-nowrap">{formatDT(row.last_seen)}</TableCell>
                    <TableCell className="px-3"><Badge variant="secondary" className="text-[10px]">{row.scan_count}</Badge></TableCell>
                    <TableCell className="px-3">
                      <Input
                        value={labels[key] || ''}
                        onChange={(e) => setLabels((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder="Label (optional)"
                        className="h-7 text-[11px]"
                        data-testid={`unmatched-scan-label-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="px-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                          onClick={() => register(row, false)} data-testid={`unmatched-scan-add-${idx}`}>
                          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                          Add card
                        </Button>
                        <PersonCombobox
                          persons={persons}
                          value={selections[key] || ''}
                          onChange={(v) => setSelections((prev) => ({ ...prev, [key]: v }))}
                          testId={`unmatched-scan-person-select-${idx}`}
                        />
                        <Button size="sm" className="h-7 text-xs bg-[#2563eb] hover:bg-[#1d4ed8]" disabled={busy || !selections[key]}
                          onClick={() => register(row, true)} data-testid={`unmatched-scan-add-allocate-${idx}`}>
                          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
                          Add + allocate
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-xs" data-testid="unmatched-scans-empty">
                    {loading ? 'Loading…' : (debouncedSearch ? 'No unmatched scans match your search.' : 'No unmatched scans in the last 30 days — every scanned card is registered.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <PaginationFooter
          page={page}
          setPage={setPage}
          totalItems={total}
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          testIdPrefix="unmatched-scans"
          scrollOnChange={false}
          disabled={loading}
        />
        <p className="text-[11px] text-muted-foreground">
          Only people without an allocated card are shown in the allocate picker. The export is import-compatible with
          the All RFID / iButton Cards importer — fill the <span className="font-medium text-foreground">assigned_type</span> (driver / staff / passenger)
          and <span className="font-medium text-foreground">assigned_person</span> (Employee / Passenger ID, or email) columns to allocate cards on import.
          Want an instant alert when an unknown card is scanned? Create an <span className="font-medium text-foreground">Unmatched RFID / iButton Scan</span> rule in Analytics &gt; Events.
        </p>
      </DialogContent>
    </Dialog>
  );
};
